/**
 * @file AppFooter.tsx
 * @brief App shell footer: branding strip, Help & Docs + Demo meeting links, year.
 *
 * @details
 * Compact branding strip at every width; quick links live in the header's
 * quick-nav row (the below-lg bottom tabs were removed 30-09-2026).
 *
 * @author DES
 * @date 19-06-2026
 */

import { CircleHelp, FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import { BrandWordmark } from "@/components/brand/BrandWordmark";
import { useBoardroomBasePath } from "@/features/employee-boardroom/useBoardroomBasePath";
import { useAuth } from "@/shared/auth/auth-context";
import { FALLBACKS, safeHttps } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";
import { cn } from "@/shared/theme/cn";
import { shellRailClass } from "./shell-rail";

/* Help & Docs = HELP_URL from Super Admin → Links & company info (B23,
   24-10-2026), the same key the phone's Help & Docs opens; until it loads
   (or without B23) the website's user guide, as before. */

export function AppFooter() {
  const year = new Date().getFullYear();
  // The demo meeting from Help (calm Home, DES 19-10-2026): Home shows its
  // card only until the first meeting; this keeps it reachable after.
  const base = useBoardroomBasePath();
  const { entitlements } = useAuth();
  const showDemo = entitlements.boardroom !== false;
  const links = usePublicConfig();
  const helpUrl = safeHttps(links.value("HELP_URL", FALLBACKS.HELP_URL), FALLBACKS.HELP_URL);

  return (
    <footer className="border-t bg-app-page/90 backdrop-blur-md supports-[backdrop-filter]:bg-app-page/75">
      {/* The mobile-style quick tabs that sat here below lg were removed
          (30-09-2026, DES: "i dont like these"); the header's quick-nav row
          now shows at every width instead. */}

      <div
        className={cn(
          shellRailClass,
          "flex items-center justify-between gap-3 py-2.5",
        )}
      >
        <BrandWordmark height={22} />
        {/* Help & Docs beside the year, as the phone drawer's link (DES
            04-10-2026). The desktop app opens it in the real browser
            (DESKTOP/src/main.js setWindowOpenHandler). */}
        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <a
            href={helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
          >
            <CircleHelp className="h-3.5 w-3.5" aria-hidden />
            Help &amp; Docs
          </a>
          {showDemo ? (
            <>
              <span aria-hidden>·</span>
              <Link
                to={`${base}/meetings/demo`}
                className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
              >
                <FlaskConical className="h-3.5 w-3.5" aria-hidden />
                Demo meeting
              </Link>
            </>
          ) : null}
          <span aria-hidden>·</span>
          <span>© {year}</span>
        </span>
      </div>
    </footer>
  );
}
